import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import playerImportService from '../../../services/playerImportService';

const TransfermarktImportModal = ({ onClose, onImportSuccess, playerType = 'player' }) => {
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [detectedSource, setDetectedSource] = useState(null);

  const handleUrlChange = (value) => {
    setUrl(value);
    setError('');
    
    // Detect source as user types
    if (value?.trim()) {
      const source = playerImportService?.getSourceName(value);
      setDetectedSource(source);
    } else {
      setDetectedSource(null);
    }
  };

  const handleImport = async () => {
    setError('');
    setSuccess('');

    if (!url?.trim()) {
      setError('Please enter a player profile URL');
      return;
    }

    if (!playerImportService?.isValidUrl(url)) {
      setError('Invalid URL format. Supported sources: Transfermarkt, Sofascore, Soccerway');
      return;
    }

    setLoading(true);

    try {
      const result = await playerImportService?.importPlayer(url);
      setSuccess(result?.message || 'Player imported successfully!');
      
      // Wait 2 seconds then close and refresh
      setTimeout(() => {
        onImportSuccess?.();
        onClose?.();
      }, 2000);
    } catch (err) {
      setError(err?.message || 'Failed to import player. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const exampleUrls = playerImportService?.getExampleUrls();
  const allExamples = [
    ...exampleUrls?.transfermarkt || [],
    ...exampleUrls?.sofascore || [],
    ...exampleUrls?.soccerway || []
  ];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg border border-border max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-card border-b border-border p-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="Download" size={20} color="var(--color-primary)" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-foreground">Import Player Profile</h2>
              <p className="text-sm text-muted-foreground">Supports Transfermarkt, Sofascore & Soccerway</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-muted transition-colors"
          >
            <Icon name="X" size={20} color="var(--color-muted-foreground)" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Instructions */}
          <div className="bg-accent/10 border border-accent/20 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Icon name="Info" size={20} color="var(--color-accent)" className="flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-semibold text-foreground mb-2">How to Import</h3>
                <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside">
                  <li>Visit Transfermarkt, Sofascore, or Soccerway and find the player</li>
                  <li>Copy the complete profile URL from your browser</li>
                  <li>Paste it in the field below (source will be auto-detected)</li>
                  <li>Click "Import Player" to create the profile</li>
                  <li>Complete any missing information manually after import</li>
                </ol>
              </div>
            </div>
          </div>

          {/* Supported Sources */}
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-muted/50 rounded-lg p-3 text-center">
              <div className="text-xs font-medium text-muted-foreground mb-1">Transfermarkt</div>
              <div className="text-xs text-muted-foreground/70">transfermarkt.com</div>
            </div>
            <div className="bg-muted/50 rounded-lg p-3 text-center">
              <div className="text-xs font-medium text-muted-foreground mb-1">Sofascore</div>
              <div className="text-xs text-muted-foreground/70">sofascore.com</div>
            </div>
            <div className="bg-muted/50 rounded-lg p-3 text-center">
              <div className="text-xs font-medium text-muted-foreground mb-1">Soccerway</div>
              <div className="text-xs text-muted-foreground/70">soccerway.com</div>
            </div>
          </div>

          {/* URL Input */}
          <div>
            <Input
              label="Player Profile URL"
              placeholder="Paste URL from Transfermarkt, Sofascore, or Soccerway"
              value={url}
              onChange={(e) => handleUrlChange(e?.target?.value)}
              disabled={loading}
            />
            {detectedSource && (
              <div className="mt-2 flex items-center gap-2 text-sm">
                <Icon name="CheckCircle" size={16} color="var(--color-success)" />
                <span className="text-success">Detected: {detectedSource}</span>
              </div>
            )}
          </div>

          {/* Example URLs */}
          <div>
            <p className="text-sm font-medium text-foreground mb-2">Example URLs (click to use):</p>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {allExamples?.map((exampleUrl, index) => {
                const source = playerImportService?.getSourceName(exampleUrl);
                return (
                  <button
                    key={index}
                    onClick={() => handleUrlChange(exampleUrl)}
                    disabled={loading}
                    className="w-full text-left px-3 py-2 bg-muted/50 hover:bg-muted rounded-lg transition-colors group"
                  >
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <span className="text-xs font-medium text-foreground">{source}</span>
                      <Icon name="Copy" size={12} color="var(--color-muted-foreground)" className="opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                    <div className="text-xs text-muted-foreground break-all">{exampleUrl}</div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-error/10 border border-error/20 rounded-lg p-3 flex items-start gap-2">
              <Icon name="AlertCircle" size={18} color="var(--color-error)" className="flex-shrink-0 mt-0.5" />
              <p className="text-sm text-error">{error}</p>
            </div>
          )}

          {/* Success Message */}
          {success && (
            <div className="bg-success/10 border border-success/20 rounded-lg p-3 flex items-start gap-2">
              <Icon name="CheckCircle" size={18} color="var(--color-success)" className="flex-shrink-0 mt-0.5" />
              <p className="text-sm text-success">{success}</p>
            </div>
          )}

          {/* Important Note */}
          <div className="bg-warning/10 border border-warning/20 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Icon name="AlertTriangle" size={20} color="var(--color-warning)" className="flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-semibold text-foreground mb-1">Important Note</h3>
                <p className="text-sm text-muted-foreground">
                  This feature creates a basic player profile with the source reference. 
                  Due to web scraping limitations, you'll need to manually complete the player's 
                  performance statistics, career history, and other detailed information after import.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-card border-t border-border p-6 flex items-center justify-end gap-3">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleImport}
            disabled={loading || !url?.trim()}
            iconName="Download"
            iconPosition="left"
          >
            {loading ? 'Importing...' : 'Import Player'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TransfermarktImportModal;